import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import { APP_VERSION } from '../utils/version';
import { useAuth } from '../contexts/AuthContext';
import NoTranslate from '../components/NoTranslate';
import {
  formatDisplayDate,
  formatDisplayDateRange,
  formatDisplayDateTime,
  resolveLeaveTypeName
} from '../utils/localeFormat';

function AdminApplications() {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    department: searchParams.get('department') || '',
    year: searchParams.get('year') || new Date().getFullYear(),
    search: ''
  });

  const [applicantSearchInput, setApplicantSearchInput] = useState('');
  const applicationsListFetchCompletedRef = useRef(false);
  const [departments, setDepartments] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [reviewModal, setReviewModal] = useState({
    show: false,
    applicationId: null,
    status: '',
    notes: ''
  });
  const [withdrawModal, setWithdrawModal] = useState({ show: false, confirmText: '' });
  const [adjustModal, setAdjustModal] = useState(false);
  const [adjustForm, setAdjustForm] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [adminActionLoading, setAdminActionLoading] = useState(false);

  useEffect(() => {
    fetchApplications();
    fetchDepartments();
    const loadMeta = async () => {
      try {
        const [lt, ap] = await Promise.all([
          api.get('/leave-types'),
          api.get('/users/approvers')
        ]);
        setLeaveTypes(lt.data || []);
        setApprovers(ap.data || []);
      } catch (e) {
        console.error(e);
      }
    };
    loadMeta();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount + meta load; avoid re-running fetchApplications identity loop
  }, []);

  useEffect(() => {
    fetchApplications();
    fetchDepartments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    // Open specific application from URL parameter
    const applicationId = searchParams.get('id');
    if (applicationId && applications.length > 0) {
      const app = applications.find(a => a.id === parseInt(applicationId));
      if (app) {
        setSelectedApplication(app);
      }
    }
  }, [applications, searchParams]);

  useEffect(() => {
    const id = setTimeout(() => {
      const q = applicantSearchInput.trim();
      setFilters(prev => (prev.search === q ? prev : { ...prev, search: q }));
    }, 300);
    return () => clearTimeout(id);
  }, [applicantSearchInput]);

  const fetchApplications = async () => {
    try {
      if (!applicationsListFetchCompletedRef.current) {
        setLoading(true);
      }
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.department) params.append('department', filters.department);
      if (filters.year) params.append('year', filters.year);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/applications/admin/all?${params}`);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
      applicationsListFetchCompletedRef.current = true;
    }
  };

  const fetchDepartments = async () => {
    try {
      // Get department list (extracted from application data)
      const response = await api.get('/applications/admin/all');
      const uniqueDepts = [...new Set(response.data.map(app => app.department))];
      setDepartments(uniqueDepts.filter(dept => dept));
    } catch (error) {
      console.error('Error fetching department list:', error);
    }
  };

  const fetchApplicationDetail = async (applicationId) => {
    try {
      const response = await api.get(`/applications/${applicationId}`);
      setSelectedApplication(response.data);
    } catch (error) {
      console.error('Error fetching application details:', error);
      toast.error('Failed to fetch application details');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openReviewModal = (applicationId, status) => {
    setReviewModal({
      show: true,
      applicationId,
      status,
      notes: ''
    });
  };

  const closeReviewModal = () => {
    setReviewModal({
      show: false,
      applicationId: null,
      status: '',
      notes: ''
    });
  };

  const handleReview = async () => {
    try {
      await api.patch(`/applications/${reviewModal.applicationId}/review`, {
        status: reviewModal.status,
        admin_notes: reviewModal.notes
      });

      toast.success(`Application ${reviewModal.status === 'approved' ? 'approved' : 'rejected'}`);
      closeReviewModal();
      fetchApplications();
      
      if (selectedApplication && selectedApplication.id === reviewModal.applicationId) {
        fetchApplicationDetail(reviewModal.applicationId);
      }
    } catch (error) {
      console.error('Application processing error:', error);
      toast.error('Failed to process application');
    }
  };

  const openWithdrawModal = () => {
    setWithdrawModal({ show: true, confirmText: '' });
  };

  const closeWithdrawModal = () => {
    setWithdrawModal({ show: false, confirmText: '' });
  };

  const handleAdminWithdraw = async () => {
    if (!selectedApplication) return;
    if (withdrawModal.confirmText !== 'delete') {
      toast.error('Type the word delete to confirm.');
      return;
    }
    try {
      setAdminActionLoading(true);
      await api.post(`/applications/${selectedApplication.id}/admin/withdraw`, {});
      toast.success('Application withdrawn');
      closeWithdrawModal();
      setSelectedApplication(null);
      fetchApplications();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || 'Failed to withdraw application');
    } finally {
      setAdminActionLoading(false);
    }
  };

  const openAdjustModal = () => {
    if (!selectedApplication) return;
    const d = selectedApplication.details || [];
    setAdjustForm({
      leave_type_id: String(selectedApplication.leave_type_id),
      start_date: String(selectedApplication.start_date).slice(0, 10),
      end_date: String(selectedApplication.end_date).slice(0, 10),
      reason: selectedApplication.reason || '',
      requested_approver_id: selectedApplication.requested_approver_id
        ? String(selectedApplication.requested_approver_id)
        : '',
      leave_details: d.length
        ? d.map(x => ({
            date: String(x.leave_date).slice(0, 10),
            period: x.period,
            hours: String(x.hours)
          }))
        : [{
            date: String(selectedApplication.start_date).slice(0, 10),
            period: 'full',
            hours: '8'
          }],
      admin_notes: ''
    });
    setAdjustModal(true);
  };

  const closeAdjustModal = () => {
    setAdjustModal(false);
    setAdjustForm(null);
  };

  const handleAdjustDetailChange = (index, field, value) => {
    setAdjustForm(prev => {
      const next = { ...prev, leave_details: [...prev.leave_details] };
      next.leave_details[index] = { ...next.leave_details[index], [field]: value };
      return next;
    });
  };

  const addAdjustDetailRow = () => {
    setAdjustForm(prev => {
      const last = prev.leave_details[prev.leave_details.length - 1];
      const d = last ? new Date(last.date) : new Date(prev.start_date);
      d.setDate(d.getDate() + 1);
      const y = d.toISOString().slice(0, 10);
      return {
        ...prev,
        leave_details: [...prev.leave_details, { date: y, period: 'full', hours: '8' }]
      };
    });
  };

  const removeAdjustDetailRow = (index) => {
    setAdjustForm(prev => ({
      ...prev,
      leave_details: prev.leave_details.filter((_, i) => i !== index)
    }));
  };

  const handleAdminAdjust = async () => {
    if (!selectedApplication || !adjustForm) return;
    if (adjustForm.leave_details.length === 0) {
      toast.error('Add at least one schedule row.');
      return;
    }
    try {
      setAdminActionLoading(true);
      const payload = {
        leave_type_id: parseInt(adjustForm.leave_type_id, 10),
        start_date: adjustForm.start_date,
        end_date: adjustForm.end_date,
        reason: adjustForm.reason || undefined,
        requested_approver_id: adjustForm.requested_approver_id
          ? parseInt(adjustForm.requested_approver_id, 10)
          : null,
        leave_details: adjustForm.leave_details.map(row => ({
          date: row.date,
          period: row.period,
          hours: parseFloat(row.hours)
        })),
        admin_notes: adjustForm.admin_notes.trim() || undefined
      };
      await api.put(`/applications/${selectedApplication.id}/admin/adjust`, payload);
      toast.success('Application updated');
      closeAdjustModal();
      fetchApplications();
      fetchApplicationDetail(selectedApplication.id);
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || error.response?.data?.error || error.message;
      toast.error(typeof msg === 'string' ? msg : 'Failed to update application');
    } finally {
      setAdminActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { text: 'Pending', class: 'badge-warning' },
      approved: { text: 'Approved', class: 'badge-success' },
      rejected: { text: 'Rejected', class: 'badge-danger' }
    };
    const statusInfo = statusMap[status] || { text: status, class: 'badge-secondary' };
    return (
      <NoTranslate className={`badge ${statusInfo.class}`}>{statusInfo.text}</NoTranslate>
    );
  };

  const formatDateRange = formatDisplayDateRange;

  const formatDateTime = formatDisplayDateTime;

  const formatLeaveTypeName = (application) => resolveLeaveTypeName(application, leaveTypes);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Application Management</h1>
          <small className="text-muted">{APP_VERSION}</small>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <h3>Filters</h3>
        <div className="grid grid-4">
          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              name="status"
              className="form-control"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Department</label>
            <select
              name="department"
              className="form-control notranslate"
              translate="no"
              value={filters.department}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Year</label>
            <select
              name="year"
              className="form-control"
              value={filters.year}
              onChange={handleFilterChange}
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="admin-applications-applicant-search">
              Applicant search
            </label>
            <input
              id="admin-applications-applicant-search"
              type="search"
              className="form-control"
              placeholder="Employee ID, Name, or Email"
              value={applicantSearchInput}
              onChange={(e) => setApplicantSearchInput(e.target.value)}
              autoComplete="off"
            />
            <small className="text-muted d-block mt-1">
              Applicant Employee ID, Name, or Email — partial match, any field.
            </small>
            <small className="text-muted d-block">{applications.length} applications</small>
          </div>
        </div>
      </div>

      <div className="grid grid-2">
        {/* Application List */}
        <div className="applications-panel">
          <h3>Application List</h3>
          {applications.length > 0 ? (
            <div className="applications-list">
              {applications.map(application => {
                const isAssignedToMe = user && application.requested_approver_id === user.id;
                return (
                <div 
                  key={application.id} 
                  className={`application-item ${selectedApplication?.id === application.id ? 'selected' : ''} ${isAssignedToMe ? 'assigned-to-me' : ''}`}
                  onClick={() => fetchApplicationDetail(application.id)}
                >
                  <div className="application-header">
                    <div className="applicant-info">
                      <NoTranslate as="strong">{application.applicant_name}</NoTranslate>
                      <NoTranslate className="employee-id">({application.employee_id})</NoTranslate>
                    </div>
                    <NoTranslate className="department">{application.department}</NoTranslate>
                    {getStatusBadge(application.status)}
                  </div>
                  
                  <div className="application-details">
                    <div className="application-id">ID: #{application.id}</div>
                    <div className="leave-type">
                      <NoTranslate>{formatLeaveTypeName(application)}</NoTranslate>
                    </div>
                    <div className="leave-period">
                      {formatDateRange(application.start_date, application.end_date)}
                    </div>
                    {application.requested_approver_name && (
                      <div className="approver-info">
                        Approver:{' '}
                        <NoTranslate>
                          {application.requested_approver_name}
                          {application.requested_approver_department && ` (${application.requested_approver_department})`}
                        </NoTranslate>
                        {isAssignedToMe && (
                          <span className="assigned-badge" title="Assigned to me">
                            👤 To Me
                          </span>
                        )}
                      </div>
                    )}
                    <div className="applied-date">
                      Applied: {formatDisplayDate(application.applied_at)}
                    </div>
                  </div>

                  {application.status === 'pending' && (
                    <div className="quick-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => openReviewModal(application.id, 'approved')}
                      >
                        Approve
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => openReviewModal(application.id, 'rejected')}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <p className="text-muted">No applications found</p>
            </div>
          )}
        </div>

        {/* Application Details */}
        <div className="application-detail-panel">
          <h3>Application Details</h3>
          {selectedApplication ? (
            <div className="application-detail card">
              <div className="detail-header">
                <NoTranslate as="h4">{formatLeaveTypeName(selectedApplication)}</NoTranslate>
                {getStatusBadge(selectedApplication.status)}
              </div>

              <div className="detail-section">
                <h5>Applicant Information</h5>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">Application ID:</span>
                    <span>#{selectedApplication.id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Name:</span>
                    <NoTranslate>{selectedApplication.applicant_name}</NoTranslate>
                  </div>
                  <div className="detail-item">
                    <span className="label">Employee ID:</span>
                    <NoTranslate>{selectedApplication.employee_id}</NoTranslate>
                  </div>
                  <div className="detail-item">
                    <span className="label">Department:</span>
                    <NoTranslate>{selectedApplication.department}</NoTranslate>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h5>Application Details</h5>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">Period:</span>
                    <span>{formatDateRange(selectedApplication.start_date, selectedApplication.end_date)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Applied Date:</span>
                    <span>{formatDateTime(selectedApplication.applied_at)}</span>
                  </div>
                  {selectedApplication.requested_approver_name && (
                    <div className="detail-item">
                      <span className="label">Approver:</span>
                      <NoTranslate>
                        {selectedApplication.requested_approver_name}
                        {selectedApplication.requested_approver_department && ` (${selectedApplication.requested_approver_department})`}
                      </NoTranslate>
                    </div>
                  )}
                  {selectedApplication.reason && (
                    <div className="detail-item full-width">
                      <span className="label">Reason:</span>
                      <span>{selectedApplication.reason}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Detailed Schedule */}
              {selectedApplication.details && selectedApplication.details.length > 0 && (
                <div className="detail-section">
                  <h5>Detailed Schedule</h5>
                  <div className="schedule-list">
                    {selectedApplication.details.map((detail, index) => (
                      <div key={index} className="schedule-item">
                        <span className="schedule-date">
                          {formatDisplayDate(detail.leave_date, {
                            month: 'short',
                            day: 'numeric',
                            weekday: 'short'
                          })}
                        </span>
                        <span className="schedule-period">
                          {detail.period === 'full' && 'Full Day'}
                          {detail.period === 'am' && 'AM'}
                          {detail.period === 'pm' && 'PM'}
                          ({detail.hours}h)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Attached Documents */}
              {(selectedApplication.certificate_filename || selectedApplication.certificate_google_drive_url) && (
                <div className="detail-section">
                  <h5>Attached Documents</h5>
                  <div className="certificate-links">
                    {selectedApplication.certificate_google_drive_url ? (
                      <a 
                        href={selectedApplication.certificate_google_drive_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="certificate-link btn btn-outline-primary"
                      >
                        📎 View Medical Certificate (Google Drive)
                      </a>
                    ) : selectedApplication.certificate_filename ? (
                      <a 
                        href={`/uploads/${selectedApplication.certificate_filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="certificate-link btn btn-outline-primary"
                      >
                        📎 View Medical Certificate
                      </a>
                    ) : null}
                  </div>
                </div>
              )}

              {(selectedApplication.compensation_attachment_filename ||
                selectedApplication.compensation_attachment_google_drive_url) &&
                selectedApplication.leave_type_code === 'COMPENSATION' && (
                <div className="detail-section">
                  <h5>Supporting Screenshot</h5>
                  <div className="certificate-links">
                    {selectedApplication.compensation_attachment_google_drive_url ? (
                      <a
                        href={selectedApplication.compensation_attachment_google_drive_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="certificate-link btn btn-outline-primary"
                      >
                        📎 View (Google Drive)
                      </a>
                    ) : (
                      <a
                        href={`/uploads/${selectedApplication.compensation_attachment_filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="certificate-link btn btn-outline-primary"
                      >
                        📎 View screenshot
                      </a>
                    )}
                  </div>
                </div>
              )}

              {(selectedApplication.other_paid_leave_document_filename ||
                selectedApplication.other_paid_leave_document_google_drive_url) &&
                selectedApplication.leave_type_code === 'OTHER_PAID' && (
                <div className="detail-section">
                  <h5>Supporting Document</h5>
                  <div className="certificate-links">
                    {selectedApplication.other_paid_leave_document_google_drive_url ? (
                      <a
                        href={selectedApplication.other_paid_leave_document_google_drive_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="certificate-link btn btn-outline-primary"
                      >
                        📎 View (Google Drive)
                      </a>
                    ) : (
                      <a
                        href={`/uploads/${selectedApplication.other_paid_leave_document_filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="certificate-link btn btn-outline-primary"
                      >
                        📎 View document
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Review Information */}
              {selectedApplication.reviewed_at && (
                <div className="detail-section">
                  <h5>Review Information</h5>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Review Date:</span>
                      <span>{formatDateTime(selectedApplication.reviewed_at)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Reviewer:</span>
                      <NoTranslate>{selectedApplication.reviewed_by_name}</NoTranslate>
                    </div>
                    {selectedApplication.admin_notes && (
                      <div className="detail-item full-width">
                        <span className="label">Admin Comment:</span>
                        <span className="admin-notes">{selectedApplication.admin_notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pending-only review */}
              {selectedApplication.status === 'pending' && (
                <div className="detail-actions">
                  <button
                    className="btn btn-success"
                    onClick={() => openReviewModal(selectedApplication.id, 'approved')}
                  >
                    Approve
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => openReviewModal(selectedApplication.id, 'rejected')}
                  >
                    Reject
                  </button>
                </div>
              )}

              <div className="detail-section">
                <h5>Admin actions</h5>
                <p className="text-muted small" style={{ marginBottom: '0.75rem' }}>
                  Withdrawing removes the application from the list. If it was approved paid annual leave, that user&apos;s
                  balance is recalculated (FIFO). Use adjust to change type, dates, or per-day slots.
                </p>
                <div className="detail-actions" style={{ borderTop: 'none', paddingTop: 0, justifyContent: 'flex-start' }}>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={openWithdrawModal}
                    disabled={adminActionLoading}
                  >
                    Withdraw application
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={openAdjustModal}
                    disabled={adminActionLoading}
                  >
                    Adjust application
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-selection">
              <p className="text-muted">Please select an application from the list on the left</p>
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {reviewModal.show && (
        <div className="modal-overlay" onClick={closeReviewModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>
              {reviewModal.status === 'approved' ? 'Approve' : 'Reject'} Application?
            </h4>
            
            <div className="form-group">
              <label className="form-label">Comment</label>
              <textarea
                className="form-control"
                rows="3"
                value={reviewModal.notes}
                onChange={(e) => setReviewModal(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Enter review reason or comment (optional)"
              />
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={closeReviewModal}>
                Cancel
              </button>
              <button 
                className={`btn ${reviewModal.status === 'approved' ? 'btn-success' : 'btn-danger'}`}
                onClick={handleReview}
              >
                {reviewModal.status === 'approved' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {withdrawModal.show && selectedApplication && (
        <div className="modal-overlay" onClick={closeWithdrawModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>Withdraw application</h4>
            <p className="text-muted small">
              Application #{selectedApplication.id} will be permanently deleted. This cannot be undone.
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="withdraw-confirm-input">Type <strong>delete</strong> to confirm</label>
              <input
                id="withdraw-confirm-input"
                type="text"
                className="form-control"
                autoComplete="off"
                value={withdrawModal.confirmText}
                onChange={(e) => setWithdrawModal((m) => ({ ...m, confirmText: e.target.value }))}
                placeholder="delete"
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeWithdrawModal} disabled={adminActionLoading}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleAdminWithdraw}
                disabled={adminActionLoading || withdrawModal.confirmText !== 'delete'}
              >
                Withdraw
              </button>
            </div>
          </div>
        </div>
      )}

      {adjustModal && adjustForm && selectedApplication && (
        <div className="modal-overlay" onClick={closeAdjustModal}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <h4>Adjust application (admin)</h4>
            <p className="text-muted small">
              Application #{selectedApplication.id}. If this is approved paid leave, balances are recalculated for this employee when you save.
            </p>

            <div className="form-group">
              <label className="form-label">Leave type</label>
              <select
                className="form-control notranslate"
                translate="no"
                value={adjustForm.leave_type_id}
                onChange={(e) => setAdjustForm((f) => ({ ...f, leave_type_id: e.target.value }))}
              >
                {leaveTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name_en || t.name_ja}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Start date</label>
                <input
                  type="date"
                  className="form-control"
                  value={adjustForm.start_date}
                  onChange={(e) => setAdjustForm((f) => ({ ...f, start_date: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">End date</label>
                <input
                  type="date"
                  className="form-control"
                  value={adjustForm.end_date}
                  onChange={(e) => setAdjustForm((f) => ({ ...f, end_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Approver</label>
              <select
                className="form-control notranslate"
                translate="no"
                value={adjustForm.requested_approver_id}
                onChange={(e) => setAdjustForm((f) => ({ ...f, requested_approver_id: e.target.value }))}
              >
                <option value="">None</option>
                {approvers.map((a) => (
                  <option key={a.id} value={a.id}>{a.full_name} — {a.department}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Reason</label>
              <textarea
                className="form-control"
                rows="2"
                value={adjustForm.reason}
                onChange={(e) => setAdjustForm((f) => ({ ...f, reason: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label mb-0">Daily schedule</label>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addAdjustDetailRow}>
                  Add row
                </button>
              </div>
              <div className="adjust-details-table">
                {adjustForm.leave_details.map((row, index) => (
                  <div key={index} className="adjust-detail-row">
                    <input
                      type="date"
                      className="form-control"
                      value={row.date}
                      onChange={(e) => handleAdjustDetailChange(index, 'date', e.target.value)}
                    />
                    <select
                      className="form-control"
                      value={row.period}
                      onChange={(e) => handleAdjustDetailChange(index, 'period', e.target.value)}
                    >
                      <option value="full">Full day</option>
                      <option value="am">AM</option>
                      <option value="pm">PM</option>
                    </select>
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      max="24"
                      className="form-control"
                      value={row.hours}
                      onChange={(e) => handleAdjustDetailChange(index, 'hours', e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      disabled={adjustForm.leave_details.length <= 1}
                      onClick={() => removeAdjustDetailRow(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Admin note (appended to admin_notes)</label>
              <textarea
                className="form-control"
                rows="2"
                value={adjustForm.admin_notes}
                onChange={(e) => setAdjustForm((f) => ({ ...f, admin_notes: e.target.value }))}
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeAdjustModal} disabled={adminActionLoading}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleAdminAdjust} disabled={adminActionLoading}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .applications-panel {
          background: white;
          border-radius: 8px;
          padding: 1rem;
          height: calc(100vh - 300px);
          overflow-y: auto;
        }
        
        .application-detail-panel {
          background: white;
          border-radius: 8px;
          padding: 1rem;
          height: calc(100vh - 300px);
          overflow-y: auto;
        }
        
        .applications-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .application-item {
          padding: 1rem;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .application-item:hover {
          background-color: #f8f9fa;
          border-color: #007bff;
        }
        
        .application-item.selected {
          background-color: #e7f3ff;
          border-color: #007bff;
          border-width: 2px;
        }
        
        .application-item.assigned-to-me {
          background-color: #fff3cd;
          border-color: #ffc107;
          border-width: 2px;
          box-shadow: 0 2px 4px rgba(255, 193, 7, 0.2);
        }
        
        .application-item.assigned-to-me:hover {
          background-color: #ffe69c;
          border-color: #ff9800;
        }
        
        .application-item.assigned-to-me.selected {
          background-color: #ffe69c;
          border-color: #ff9800;
        }
        
        .application-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.5rem;
        }
        
        .applicant-info {
          display: flex;
          flex-direction: column;
        }
        
        .employee-id {
          font-size: 0.9rem;
          color: #6c757d;
        }
        
        .assigned-badge {
          display: inline-block;
          margin-left: 0.5rem;
          padding: 0.2rem 0.5rem;
          background-color: #ffc107;
          color: #856404;
          font-size: 0.75rem;
          font-weight: 600;
          border-radius: 4px;
          white-space: nowrap;
        }
        
        .department {
          font-size: 0.9rem;
          color: #6c757d;
        }
        
        .application-details {
          margin-bottom: 0.5rem;
        }
        
        .leave-type {
          font-weight: 500;
          color: #007bff;
          margin-bottom: 0.25rem;
        }
        
        .leave-period {
          color: #495057;
          margin-bottom: 0.25rem;
        }
        
        .approver-info {
          font-size: 0.85rem;
          color: #495057;
          margin-bottom: 0.25rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        
        .approver-info .assigned-badge {
          margin-left: 0;
        }
        
        .applied-date {
          font-size: 0.8rem;
          color: #6c757d;
        }
        
        .quick-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }
        
        .btn-sm {
          padding: 0.25rem 0.5rem;
          font-size: 0.875rem;
        }
        
        .application-detail {
          border: none;
          box-shadow: none;
          padding: 0;
        }
        
        .detail-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #dee2e6;
        }
        
        .detail-section {
          margin-bottom: 1.5rem;
        }
        
        .detail-section h5 {
          margin-bottom: 0.75rem;
          color: #495057;
          border-bottom: 1px solid #dee2e6;
          padding-bottom: 0.5rem;
        }
        
        .detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }
        
        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        
        .detail-item.full-width {
          grid-column: 1 / -1;
        }
        
        .detail-item .label {
          font-weight: 500;
          color: #6c757d;
          font-size: 0.9rem;
        }
        
        .admin-notes {
          background-color: #f8f9fa;
          padding: 0.5rem;
          border-radius: 4px;
          border-left: 3px solid #ffc107;
        }
        
        .schedule-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .schedule-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem;
          background-color: #f8f9fa;
          border-radius: 4px;
        }
        
        .schedule-date {
          font-weight: 500;
          color: #495057;
        }
        
        .schedule-period {
          color: #007bff;
        }
        
        .certificate-link {
          display: inline-block;
          text-decoration: none;
        }
        
        .certificate-links {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .detail-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          padding-top: 1rem;
          border-top: 1px solid #dee2e6;
        }
        
        .no-selection {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
        }
        
        .empty-state {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
        }
        
        .search-result {
          padding: 0.5rem;
          background-color: #f8f9fa;
          border-radius: 4px;
          font-weight: 500;
          color: #495057;
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: white;
          padding: 2rem;
          border-radius: 8px;
          min-width: 400px;
          max-width: 90vw;
        }
        
        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 1.5rem;
        }
        
        .modal-content.modal-wide {
          min-width: 560px;
          max-width: 96vw;
          max-height: 90vh;
          overflow-y: auto;
        }

        .adjust-details-table {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .adjust-detail-row {
          display: grid;
          grid-template-columns: 1fr 100px 80px auto;
          gap: 0.5rem;
          align-items: center;
        }

        @media (max-width: 768px) {
          .adjust-detail-row {
            grid-template-columns: 1fr;
          }
          .grid-2 {
            grid-template-columns: 1fr;
          }
          
          .applications-panel,
          .application-detail-panel {
            height: auto;
            max-height: 400px;
          }
          
          .detail-grid {
            grid-template-columns: 1fr;
          }
          
          .quick-actions {
            flex-direction: column;
          }
          
          .modal-content {
            min-width: auto;
            margin: 1rem;
          }
        }
      `}</style>
    </div>
  );
}

export default AdminApplications;